import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FieldHelp = styled.span`
  font-size: 0.85rem;
  color: var(--gray-600);
`;

const CheckboxLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const ExampleContainer = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 1rem;
  margin-top: 0.5rem;
`;

const ScenarioLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
`;

const FormulaLine = styled.div`
  font-family: monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #495057;
`;

const ResultLine = styled.div`
  font-family: monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #2e7d32;
  font-weight: 600;
`;

const fmtUsd = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * Live example calculation — mirrors the math the episode-aggregator runs whenever the
 * resolver picks this rule for a claim:
 *   adjustedExpected = preSequestrationExpected − round(preSequestrationExpected × percent)
 *
 * Shows both a round-number scenario ($10,000) and the SHO Bucket B cohort row that
 * motivated CLE-2295 ($3,146.91 → $3,096.56 at 1.6%), so reviewers can spot-check the
 * rule against a real Medicare/MA remit.
 */
function SequestrationExample({ percent }: { percent: number }) {
  const percentLabel = `${(percent * 100).toFixed(2)}%`;
  const scenarios = [
    { label: "Round-number scenario", expectedCents: 1000000 },
    { label: "SHO Bucket B cohort (CLE-2295)", expectedCents: 314691 },
  ];

  return (
    <ExampleContainer>
      <ScenarioLabel>Example Calculation</ScenarioLabel>
      <FormulaLine>
        Applies only when the claim carries a CO-253 federal-sequestration
        adjustment (line- or claim-level).
      </FormulaLine>
      <FormulaLine>
        Reduction = {percentLabel} of pre-sequestration expected
      </FormulaLine>
      {scenarios.map((s) => {
        const discount = Math.round(s.expectedCents * percent);
        const adjusted = s.expectedCents - discount;
        return (
          <div key={s.label} style={{ marginTop: "0.75rem" }}>
            <ScenarioLabel>{s.label}</ScenarioLabel>
            <FormulaLine>
              Pre-sequestration expected = {fmtUsd(s.expectedCents)}
            </FormulaLine>
            <FormulaLine>
              Discount = {fmtUsd(s.expectedCents)} × {percentLabel} ={" "}
              {fmtUsd(discount)}
            </FormulaLine>
            <ResultLine>
              Adjusted expected = {fmtUsd(s.expectedCents)} − {fmtUsd(discount)}{" "}
              = {fmtUsd(adjusted)}
            </ResultLine>
          </div>
        );
      })}
    </ExampleContainer>
  );
}

/**
 * Form data for both create and edit. Create uses `providerIds[]`/`payerIds[]` to drive
 * the multi-select fan-out (N×M rows per submit). Edit collapses the multi-selects to a
 * single row, mapping the row's providerId/payerId into the arrays so the same form
 * component can render both flows.
 */
export interface ISequestrationRulesetFormData {
  providerIds: string[];
  payerIds: number[];
  /** Stored as a decimal (e.g., 0.016 = 1.6%). UI displays as percent (×100). */
  percent: number;
  isDefault: boolean;
  contractLanguage: string;
}

export const INITIAL_SEQUESTRATION_RULESET_FORM_DATA: ISequestrationRulesetFormData =
  {
    providerIds: [],
    payerIds: [],
    percent: 0.016,
    isDefault: false,
    contractLanguage: "",
  };

interface ISequestrationRulesetFormProps {
  title: string;
  submitLabel: string;
  formData: ISequestrationRulesetFormData;
  onChange: (data: ISequestrationRulesetFormData) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  /**
   * In edit mode we lock isDefault (default rows can't be re-scoped) and collapse the
   * multi-selects to single selectors. In create mode the multi-select fan-out is the
   * point.
   */
  mode: "create" | "edit";
  /** Locked in edit mode — the default row's percent and language are editable but its scope is fixed. */
  scopeLocked?: boolean;
}

export function SequestrationRulesetForm({
  title,
  submitLabel,
  formData,
  onChange,
  onSubmit,
  isSubmitting,
  mode,
  scopeLocked,
}: ISequestrationRulesetFormProps) {
  const navigate = useNavigate();
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();

  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];
    return providers.data.map((p: { id: string; name: string }) => ({
      label: p.name,
      value: p.id,
    }));
  }, [providers]);

  const payerOptions = useMemo(() => {
    return (payers?.data ?? []).map((p: { id: number; name: string }) => ({
      label: p.name,
      value: p.id,
    }));
  }, [payers]);

  const handleChange = <K extends keyof ISequestrationRulesetFormData>(
    field: K,
    value: ISequestrationRulesetFormData[K]
  ) => {
    onChange({ ...formData, [field]: value });
  };

  // Non-default rules need at least one provider OR payer (CLE-2403: payer-only rules are
  // valid — providers empty means "all providers for the selected payers"). A fully
  // unscoped rule is the global default, managed via the isDefault checkbox.
  const isValid =
    formData.percent >= 0 &&
    formData.percent < 1 &&
    (formData.isDefault ||
      formData.providerIds.length > 0 ||
      formData.payerIds.length > 0) &&
    !isSubmitting;

  const percentDisplay = Number((formData.percent * 100).toFixed(4));

  return (
    <div className="p-4">
      <div className="flex align-items-center mb-4">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text mr-2"
          onClick={() => navigate("/payer-rules")}
          aria-label="Back"
        />
        <h2 className="m-0">{title}</h2>
      </div>

      <Message
        severity="info"
        className="mb-4"
        text="The Sequester rule reduces expectedAmount by the configured percent for any claim that carries a CO-253 (federal sequestration) adjustment — and only when the claim's payer type is Medicare or Medicare Advantage (Commercial claims are never shaved, regardless of rule scope). The resolver picks the most-specific match for the claim's (provider, payer) tuple: provider+payer row first, then provider-only, then payer-only, then the global default."
      />

      <Card className="mb-4">
        <FieldGrid>
          <Field>
            <label htmlFor="percent">Sequestration reduction (%)</label>
            <InputNumber
              inputId="percent"
              value={percentDisplay}
              onValueChange={(e) =>
                handleChange("percent", (e.value ?? 0) / 100)
              }
              mode="decimal"
              minFractionDigits={2}
              maxFractionDigits={4}
              min={0}
              max={99.99}
              suffix=" %"
            />
            <FieldHelp>
              1.60% is the effective Medicare/MA rate (2% federal cut × 80%
              Medicare-paid portion).
            </FieldHelp>
          </Field>
          <Field>
            <SequestrationExample percent={formData.percent} />
          </Field>
        </FieldGrid>

        <CheckboxLine>
          <Checkbox
            inputId="isDefault"
            checked={formData.isDefault}
            disabled={mode === "edit" || scopeLocked}
            onChange={(e) => handleChange("isDefault", !!e.checked)}
          />
          <label htmlFor="isDefault">
            Default rule (applies to all providers and payers unless overridden)
          </label>
        </CheckboxLine>

        {!formData.isDefault && mode === "create" && (
          <FieldGrid>
            <Field>
              <label htmlFor="providerIds">Providers</label>
              <MultiSelect
                inputId="providerIds"
                value={formData.providerIds}
                options={providerOptions}
                onChange={(e) => handleChange("providerIds", e.value ?? [])}
                placeholder="Select providers (or leave empty for 'all providers')"
                display="chip"
                filter
                filterPlaceholder="Search providers..."
              />
              <FieldHelp>
                Leave empty to create one payer-only row per selected payer that
                applies to all providers. At least one provider or payer is
                required.
              </FieldHelp>
            </Field>
            <Field>
              <label htmlFor="payerIds">Payers</label>
              <MultiSelect
                inputId="payerIds"
                value={formData.payerIds}
                options={payerOptions}
                onChange={(e) => handleChange("payerIds", e.value ?? [])}
                placeholder="Select payers (or leave empty for 'all payers')"
                display="chip"
                filter
                filterPlaceholder="Search payers..."
              />
              <FieldHelp>
                Leave empty to create one row per provider that applies to all
                payers. With N providers and M payers, N×M rows are created.
              </FieldHelp>
            </Field>
          </FieldGrid>
        )}

        {!formData.isDefault && mode === "edit" && (
          <FieldGrid>
            <Field>
              <label htmlFor="providerId">Provider</label>
              <Dropdown
                inputId="providerId"
                value={formData.providerIds[0] ?? null}
                options={[
                  { label: "All providers", value: null },
                  ...providerOptions,
                ]}
                onChange={(e) =>
                  handleChange("providerIds", e.value ? [e.value] : [])
                }
                placeholder="Select a provider"
                filter
                disabled={scopeLocked}
              />
              <FieldHelp>
                Leave as &quot;All providers&quot; to apply this rule to every
                provider for the selected payer (a provider or payer is still
                required).
              </FieldHelp>
            </Field>
            <Field>
              <label htmlFor="payerId">Payer</label>
              <Dropdown
                inputId="payerId"
                value={formData.payerIds[0] ?? null}
                options={[
                  { label: "All payers", value: null },
                  ...payerOptions,
                ]}
                onChange={(e) =>
                  handleChange(
                    "payerIds",
                    e.value === null || e.value === undefined ? [] : [e.value]
                  )
                }
                placeholder="Select a payer"
                filter
                disabled={scopeLocked}
              />
              <FieldHelp>
                Leave as &quot;All payers&quot; to apply this rule to every
                payer for this provider.
              </FieldHelp>
            </Field>
          </FieldGrid>
        )}

        <Field>
          <label htmlFor="contractLanguage">Contract language (optional)</label>
          <InputTextarea
            id="contractLanguage"
            value={formData.contractLanguage}
            onChange={(e) => handleChange("contractLanguage", e.target.value)}
            rows={3}
            autoResize
            maxLength={10000}
          />
          <FieldHelp>
            Free-text note for the contract excerpt or rationale behind this
            rule.
          </FieldHelp>
        </Field>
      </Card>

      <Footer>
        <Button
          label="Cancel"
          className="p-button-text"
          onClick={() => navigate("/payer-rules")}
          disabled={isSubmitting}
        />
        <Button
          label={submitLabel}
          onClick={onSubmit}
          disabled={!isValid}
          loading={isSubmitting}
        />
      </Footer>
    </div>
  );
}
